import { ReactComponent as AssignmentIcon } from '../../Common/BootstrapIcons/assignment-late.svg';
import { ReactComponent as CopyIcon } from '../../Common/BootstrapIcons/copy.svg';
import { ReactComponent as DownloadIcon } from '../../Common/BootstrapIcons/download.svg';
import { ReactComponent as EditIcon } from '../../Common/BootstrapIcons/edit.svg';
import { ReactComponent as HistoryIcon } from '../../Common/BootstrapIcons/history.svg';
import { ReactComponent as OffIcon } from '../../Common/BootstrapIcons/toggle-off.svg';
import { ReactComponent as OnIcon } from '../../Common/BootstrapIcons/toggle-on.svg';
import { ReactComponent as RemoveIcon } from '../../Common/BootstrapIcons/close.svg';
import { ReactComponent as ExportIcon } from '../../Common/BootstrapIcons/caret-up-fill.svg';
import { ReactComponent as ViewIcon } from '../../Common/BootstrapIcons/view.svg';
import { ReactComponent as ViewReleasedIcon } from '../../Common/BootstrapIcons/shield-check.svg';
import { ReactComponent as ChangeDataSourceFromToIcon } from '../../Common/BootstrapIcons/swap.svg';
import appRoutes from '../../../infrastructure/constants/appRoutes';
import ColumnDefinition from '../../../infrastructure/types/DataGrid/ColumnDefinition';
import CustomIcon from '../../Common/BootstrapIcons/Custom/CustomIcon';
import history from '../../../infrastructure/helpers/react/history';
import iconTooltip from '../../Common/BootstrapIcons/Tooltip/IconTooltip';
import identityStorage from '../../../infrastructure/authorization/identityStorage';
import PromiseButton from '../../Common/PromiseButton';
import React from 'react';
import ReportConfigGridItem from '../../../types/report/ReportConfigGridItem';
import urlHelper from '../../../infrastructure/helpers/common/urlHelper';
import writeUserRoles from '../../../infrastructure/constants/identity/writeUserRoles';
import reportRegimeHelper from '../../../infrastructure/helpers/report/reportRegime/reportRegimeHelper';
import Spinner from '../../Common/Spinner/Spinner';
import InfoIcon from '../../Common/InfoIcon/InfoIcon';
import clientWriteUserRoles from '../../../infrastructure/constants/identity/clientWriteUserRole';
import EditableText from '../../Common/EditableText/EditableText';
import stringHelper from '../../../infrastructure/helpers/common/stringHelper';

const uniqueName = 'report-config-grid';

const resolveIcon = (isEnabled: boolean): React.FunctionComponent<React.SVGProps<SVGSVGElement>> => isEnabled ? OnIcon : OffIcon;

const resolveAction = (isEnabled: boolean): string => isEnabled ? 'archive' : 'unarchive';

const getColumns = (onEditClick: (id: number) => void,
    onCopyClick: (config: ReportConfigGridItem) => void,
    enabled: boolean,
    onDownloadClick: (id: number) => Promise<void>,
    onRemoveClick: (config: ReportConfigGridItem) => void,
    onCopyConfigSourceClick: (id: number) => Promise<void>,
    showSpinnerForId: number,
    showSpinnerForDownloadId: number,
    onInfoClick: (config: ReportConfigGridItem) => void,
    onViewReleasedClick: (id: number) => void,
    onChangeDataSourceClick: (config: ReportConfigGridItem) => void,
    onArchiveClick: (config: ReportConfigGridItem, action: string) => Promise<any>,
    isFourEyesReleaseProcessEnabled?: boolean,
    showSpinnerForChangeDataSourceId?: number,
    showSpinnerForArchiveId?: number,
    showSpinnerForRemoveConfigId?: number
): ColumnDefinition<ReportConfigGridItem>[] => {

    let identity = identityStorage.get();

    let hasWriteRole = writeUserRoles.includes(identity.userRole);

    let isNotSuperAdmin = clientWriteUserRoles.includes(identity.userRole);

    return [
        {
            header: 'Id',
            sortKey: 'Id',
            getContent: x => x.id.toString(),
            width: '4%'
        },
        {
            header: 'Name',
            sortKey: 'Name',
            getContent: x => <a href={`${appRoutes.reporting.edit}?id=${x.id}`} className="cursor-pointer transparent-button" onClick={() => onEditClick(x.id)} >{x.name}</a>,
            className: 'break-all',
            width: '22%'
        },
        {
            header: 'Description',
            sortKey: 'Description',
            getContent: x => <EditableText className='max-width-90-percents' value={x.description} multiline={true} disabled={true} />,
            width: '27%'
        },
        {
            header: 'Regime',
            sortKey: 'Regime',
            getContent: x => reportRegimeHelper.getRegimeDisplayName(x.regime),
            width: '9%',
            className: 'break-all'
        },
        {
            header: 'Stage',
            sortKey: 'IsReleased',
            getContent: x => x.isReleased ? 'Released' : 'Unreleased',
            width: '8%'
        },
        {
            header: 'Version',
            sortKey: 'ReleaseNumber',
            getContent: x => x.releaseNumber || '-',
            width: '5%'
        },
        {
            header: 'Actions',
            getContent: x =>
                <div className='display-flex align-items-end'>
                    <button className="custom-icon-button" onClick={() => history.push(urlHelper.buildUrl(appRoutes.reporting.changeLog, { isFourEyesReleaseProcessEnabled: isFourEyesReleaseProcessEnabled, name: x.name, id: x.id, redirect: encodeURIComponent(appRoutes.reporting.home) }))}>
                        <CustomIcon icon={HistoryIcon} title={iconTooltip.changeLog} />
                    </button>
                    <div className='display-flex align-items-center padding-right-4px'>
                        <a className='height-30px' href={`${appRoutes.reporting.edit}?id=${x.id}`} onClick={() => onEditClick(x.id)} >
                            {
                                hasWriteRole ?
                                    <CustomIcon icon={EditIcon} title={iconTooltip.edit} />
                                    : <CustomIcon icon={ViewIcon} title={iconTooltip.view} />
                            }
                        </a>
                    </div>
                    <button className="custom-icon-button" onClick={() => onViewReleasedClick(x.id)}>
                        <CustomIcon icon={ViewReleasedIcon} title={iconTooltip.viewReleased} />
                    </button>
                    <button className="custom-icon-button button-spinner-font-size" onClick={() => onDownloadClick(x.id)}>
                        {
                            x.id === showSpinnerForDownloadId ? <Spinner /> :
                                <CustomIcon icon={DownloadIcon} title={iconTooltip.download} />
                        }
                    </button>
                    {
                        hasWriteRole &&
                        <PromiseButton className="custom-icon-button button-spinner-font-size" enableOnErrorOnly={true} task={() => onArchiveClick(x, resolveAction(x.isEnabled))}>
                            {
                                x.id === showSpinnerForArchiveId ? <Spinner /> :
                                    <CustomIcon icon={resolveIcon(x.isEnabled)} title={resolveAction(x.isEnabled)} />
                            }
                        </PromiseButton>
                    }
                    {
                        hasWriteRole &&
                        <button className="custom-icon-button" onClick={() => onCopyClick(x)}>
                            <CustomIcon icon={CopyIcon} title={iconTooltip.copy} />
                        </button>
                    }
                    {
                        isNotSuperAdmin && hasWriteRole &&
                        <button className="custom-icon-button button-spinner-font-size" onClick={async () => await onCopyConfigSourceClick(x.id)}>
                            {
                                x.id === showSpinnerForId ? <Spinner /> :
                                    <CustomIcon icon={ExportIcon} title={iconTooltip.copyReportSource} />
                            }
                        </button>
                    }
                    <button className="custom-icon-button" onClick={() => history.push(urlHelper.buildUrl(appRoutes.reporting.actionLog, { id: x.id }))}>
                        <CustomIcon icon={AssignmentIcon} title={iconTooltip.actionLog} />
                    </button>
                    <button type="button" className="custom-icon-button button-spinner-font-size" onClick={() => onChangeDataSourceClick(x)}>
                        {
                            x.id === showSpinnerForChangeDataSourceId ? <Spinner /> :
                                <CustomIcon icon={ChangeDataSourceFromToIcon} title={iconTooltip.changeDataSourceFromToIcon} />
                        }
                    </button>
                    {
                        hasWriteRole &&
                        !enabled &&
                        <button className="custom-icon-button button-spinner-font-size" onClick={() => onRemoveClick(x)}>
                            {
                                x.id === showSpinnerForRemoveConfigId ? <Spinner /> :
                                    <CustomIcon icon={RemoveIcon} title={iconTooltip.remove} />
                            }
                        </button>
                    }
                    <button className="custom-icon-button" onClick={() => onInfoClick(x)}>
                        <InfoIcon info={iconTooltip.configInfo} />
                    </button>
                </div>,
            className: 'display-flex',
        }
    ];
};

export { getColumns, uniqueName };